import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  inject,
} from '@angular/core';

/**
 * Adds top/bottom fade hints to a scrollable element so it is obvious there is
 * more content to reveal. It sets two CSS custom properties on the host:
 *   --fade-top    0px when scrolled to the very top, otherwise the fade size
 *   --fade-bottom 0px when scrolled to the very bottom, otherwise the fade size
 * The element's own `mask-image` consumes them (see header.component.scss), so
 * the edge softly dissolves only on the side that can still scroll.
 *
 * Everything runs outside Angular, so scrolling never triggers change
 * detection.
 *
 * Usage: <div class="links" scrollFade> ... </div>
 */
@Directive({
  selector: '[scrollFade]',
  standalone: true,
})
export class ScrollFadeDirective implements AfterViewInit, OnDestroy {
  private readonly el: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly zone = inject(NgZone);

  /** Fade height in px. */
  private readonly fade = 18;
  private ro?: ResizeObserver;
  private mo?: MutationObserver;

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const node = this.el.nativeElement;
      node.addEventListener('scroll', this.update, { passive: true });

      // Content or size changes both move the scroll extremes.
      this.ro = new ResizeObserver(this.update);
      this.ro.observe(node);
      this.mo = new MutationObserver(this.update);
      this.mo.observe(node, { childList: true, subtree: true });

      this.update();
    });
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('scroll', this.update);
    this.ro?.disconnect();
    this.mo?.disconnect();
  }

  private readonly update = (): void => {
    const node = this.el.nativeElement;
    const max = node.scrollHeight - node.clientHeight;
    // 1px slack so sub-pixel rounding does not leave a fade stuck on at an end.
    const top = node.scrollTop > 1 ? this.fade : 0;
    const bottom = node.scrollTop < max - 1 ? this.fade : 0;
    node.style.setProperty('--fade-top', `${top}px`);
    node.style.setProperty('--fade-bottom', `${bottom}px`);
  };
}
